'use client';

import { Element, Ion, Isotope, RadiationType } from '@/data/elements';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Radiation, Shield, Atom, FileText, Crown, Heart, Zap, Mountain } from 'lucide-react';
import dynamic from 'next/dynamic';
import { naturalFormsData } from '@/data/natural-forms-data';
import { Suspense, useState } from 'react';
import { usePremium } from '@/contexts/PremiumContext';
import { useFavorites } from '@/contexts/FavoritesContext';
import { FeatureLockOverlay } from './FeatureLockOverlay';
import { exportElementToPDF } from '@/lib/pdfExport';
import { toast } from 'react-hot-toast';
import { useTranslations, useLocale } from 'next-intl';
import { getLocalizedElementData, translateHalfLife } from '@/data/elements-en';
import { isotopeUsesEn } from '@/data/isotopes-uses-en';
import { elementIonsEn } from '@/data/ions-data-en';
import { naturalFormsDataEn } from '@/data/natural-forms-data-en';

const Atom3D = dynamic(() => import('./Atom3D').then(m => ({ default: m.Atom3D })), { ssr: false });
const Mineral3D = dynamic(() => import('./Mineral3D').then(m => ({ default: m.Mineral3D })), { ssr: false });

interface ElementDialogProps {
  element: Element | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ElementDialog({ element, open, onOpenChange }: ElementDialogProps) {
  const { isPremium } = usePremium();
  const { isFavorite, toggleFavorite } = useFavorites();
  const [isExporting, setIsExporting] = useState(false);
  const t = useTranslations('Element');
  const locale = useLocale();

  if (!element) return null;

  const localizedData = getLocalizedElementData(element.atomicNumber, locale);
  const displayName = localizedData?.name ?? element.name;
  const displayDescription = localizedData?.description ?? element.description;
  const displayUses = localizedData?.uses ?? element.uses;

  const favorite = isFavorite(element.id.toString());

  const handleExportPDF = () => {
    if (!isPremium) {
      toast.error(t('exportPdfPremium'));
      return;
    }

    setIsExporting(true);
    try {
      exportElementToPDF(element);
      toast.success(t('pdfDownloaded', { name: displayName }));
    } catch (error) {
      toast.error(t('exportPdfError'));
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const categoryNames: Record<string, string> = {
    'alkali_metal': t('categoryAlkaliMetal'),
    'alkaline_earth_metal': t('categoryAlkalineEarthMetal'),
    'transition_metal': t('categoryTransitionMetal'),
    'post_transition_metal': t('categoryPostTransitionMetal'),
    'metalloid': t('categoryMetalloid'),
    'nonmetal': t('categoryNonmetal'),
    'halogen': t('categoryHalogen'),
    'noble_gas': t('categoryNobleGas'),
    'lanthanide': t('categoryLanthanide'),
    'actinide': t('categoryActinide'),
  };

  const stateLabels: Record<string, string> = {
    'solid': t('stateSolid'),
    'liquid': t('stateLiquid'),
    'gas': t('stateGas'),
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-4xl w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto bg-[#0a192f] border-2 border-[#233554]">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div
                className="text-5xl sm:text-6xl font-bold shrink-0"
                style={{
                  color: element.color,
                  textShadow: `0 0 30px ${element.color}80`,
                }}
              >
                {element.symbol}
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-2xl sm:text-3xl text-white mb-2 truncate">
                  {displayName}
                </DialogTitle>
                <div className="flex flex-wrap gap-2">
                  <Badge
                    style={{
                      backgroundColor: `${element.color}20`,
                      color: element.color,
                      borderColor: element.color,
                    }}
                  >
                    {categoryNames[element.category]}
                  </Badge>
                  <Badge variant="outline" className="border-[#64ffda] text-[#64ffda]">
                    {stateLabels[element.state]}
                  </Badge>
                  <Badge variant="secondary">
                    Z: {element.atomicNumber}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:ml-auto shrink-0">
              <Button
                onClick={() => {
                  toggleFavorite(element.id.toString());
                  toast.success(
                    favorite
                      ? t('removedFromFavorites', { name: displayName })
                      : t('addedToFavorites', { name: displayName }),
                    { icon: favorite ? '💔' : '❤️' }
                  );
                }}
                variant="outline"
                size="icon"
                className={`
                  transition-all duration-300
                  ${favorite
                    ? 'bg-red-500/20 border-red-500 hover:bg-red-500/30'
                    : 'border-[#64ffda] hover:bg-[#64ffda]/10'
                  }
                `}
                aria-label={favorite ? t('removeFromFavorites') : t('addToFavorites')}
              >
                <Heart
                  className={`h-4 w-4 transition-all duration-300 ${
                    favorite ? 'fill-red-500 text-red-500' : 'text-[#64ffda]'
                  }`}
                />
              </Button>
              <Button
                onClick={handleExportPDF}
                disabled={isExporting}
                className={
                  isPremium
                    ? 'bg-gradient-to-r from-[#64ffda] to-[#82aaff] text-[#0a192f] hover:opacity-90'
                    : 'bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-[#0a192f] hover:opacity-90'
                }
              >
                {isPremium ? (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    {isExporting ? t('exporting') : 'Export PDF'}
                  </>
                ) : (
                  <>
                    <Crown className="w-4 h-4 mr-2" />
                    Premium
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* 3D Visualization */}
          <div>
            <h3 className="text-lg font-semibold text-[#64ffda] mb-3">
              {t('visualization3D')}
            </h3>
            {element.atomicNumber > 20 && !isPremium ? (
              <FeatureLockOverlay featureName={t('full3DVisualization')}>
                <Suspense fallback={<div className="w-full h-[300px] bg-black/40 rounded-lg animate-pulse" />}>
                  <Atom3D color={element.color} atomicNumber={element.atomicNumber} />
                </Suspense>
              </FeatureLockOverlay>
            ) : (
              <Suspense fallback={<div className="w-full h-[300px] bg-black/40 rounded-lg animate-pulse" />}>
                <Atom3D color={element.color} atomicNumber={element.atomicNumber} />
              </Suspense>
            )}
          </div>

          {/* Properties */}
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-[#64ffda] mb-3">
                {t('physicalProperties')}
              </h3>
              <div className="space-y-2 text-sm">
                <PropertyRow label={t('atomicMass')} value={`${element.atomicWeight.toFixed(5)} u`} />
                {element.density && (
                  <PropertyRow label={t('density')} value={`${element.density} g/cm³`} />
                )}
                {element.meltingPoint && (
                  <PropertyRow label={t('meltingPoint')} value={`${element.meltingPoint} K`} />
                )}
                {element.boilingPoint && (
                  <PropertyRow label={t('boilingPoint')} value={`${element.boilingPoint} K`} />
                )}
                {element.electronegativity && (
                  <PropertyRow label={t('electronegativity')} value={element.electronegativity.toString()} />
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#64ffda] mb-3">
                {t('electronConfiguration')}
              </h3>
              <p className="text-white font-mono bg-black/40 p-3 rounded-lg border border-[#233554]">
                {element.electronConfiguration}
              </p>
            </div>
          </div>
        </div>

        {/* Description & Uses */}
        <div className="mt-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-[#64ffda] mb-2">{t('description')}</h3>
            <p className="text-[#a8b2d1] leading-relaxed">{displayDescription}</p>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-[#64ffda] mb-2">{t('uses')}</h3>
            <p className="text-[#a8b2d1] leading-relaxed">{displayUses}</p>
          </div>

          {element.discoveredBy && (
            <div className="flex flex-wrap gap-6 p-4 bg-black/30 rounded-lg border border-[#233554]">
              <div>
                <span className="text-sm text-[#64ffda]">{t('discoveredBy')}</span>
                <p className="text-white font-medium">{element.discoveredBy}</p>
              </div>
              {element.yearDiscovered && (
                <div>
                  <span className="text-sm text-[#64ffda]">{t('year')}</span>
                  <p className="text-white font-medium">{element.yearDiscovered}</p>
                </div>
              )}
            </div>
          )}

          {/* Isotopes */}
          {element.isotopes && element.isotopes.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-[#64ffda] mb-3 flex items-center gap-2">
                <Atom className="w-5 h-5" />
                {t('isotopes')} ({element.isotopes.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {element.isotopes.map((isotope, index) => (
                  <IsotopeCard key={index} isotope={isotope} elementColor={element.color} atomicNumber={element.atomicNumber} />
                ))}
              </div>
            </div>
          )}

          {/* Natural Form */}
          {naturalFormsData[element.atomicNumber] && (() => {
            const nf = naturalFormsData[element.atomicNumber];
            const nfEn = locale === 'en' ? naturalFormsDataEn[element.atomicNumber] : null;
            return (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-[#64ffda] mb-3 flex items-center gap-2">
                  <Mountain className="w-5 h-5" />
                  {t('naturalForm')}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-black/30 border border-[#233554]">
                      <p className="text-xs text-[#64ffda] font-semibold mb-1">{t('appearance')}</p>
                      <p className="text-sm text-[#a8b2d1]">{nfEn?.appearance ?? nf.appearance}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-black/30 border border-[#233554]">
                      <p className="text-xs text-[#64ffda] font-semibold mb-1">{t('naturalFormDescription')}</p>
                      <p className="text-sm text-[#a8b2d1] leading-relaxed">{nfEn?.description ?? nf.description}</p>
                    </div>
                    {nf.mainMinerals.length > 0 && (
                      <div className="p-3 rounded-lg bg-black/30 border border-[#233554]">
                        <p className="text-xs text-[#64ffda] font-semibold mb-2">{t('mainMinerals')}</p>
                        <div className="flex flex-wrap gap-2">
                          {nf.mainMinerals.map((m, i) => (
                            <div key={i} className="flex flex-col rounded bg-[#112240] px-2 py-1 border border-[#233554]">
                              <span className="text-xs text-white font-medium">{nfEn?.mainMinerals[i]?.name ?? m.name}</span>
                              <span className="text-[10px] text-[#64ffda] font-mono">{m.formula}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    {!nf.sprites && (
                      <p className="text-xs text-[#a8b2d1] mb-2 text-center">{t('rotate3D')}</p>
                    )}
                    <Suspense fallback={<div className="w-full h-[220px] bg-black/40 rounded-lg animate-pulse" />}>
                      <Mineral3D
                        crystalType={nf.crystalType}
                        mineralColor={nf.mineralColor}
                        metalness={nf.metalness}
                        roughness={nf.roughness}
                        transparent={nf.transparent}
                        opacity={nf.opacity}
                        sprites={nf.sprites}
                      />
                    </Suspense>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Ions */}
          {element.ions && element.ions.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-[#64ffda] mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                {t('ionization')} ({element.ions.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {element.ions.map((ion, index) => (
                  <IonCard key={index} ion={ion} elementColor={element.color} atomicNumber={element.atomicNumber} ionIndex={index} />
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function IsotopeCard({ isotope, elementColor, atomicNumber }: { isotope: Isotope; elementColor: string; atomicNumber: number }) {
  const t = useTranslations('Element');
  const locale = useLocale();
  const displayHalfLife = translateHalfLife(isotope.halfLife, locale);
  const displayUses = locale === 'en'
    ? (isotopeUsesEn[atomicNumber]?.[isotope.massNumber] ?? isotope.uses)
    : isotope.uses;

  const getDecayModeColor = (mode: RadiationType): string => {
    switch (mode) {
      case 'stable': return '#4ade80';
      case 'alpha': return '#f87171';
      case 'beta-':
      case 'beta+': return '#60a5fa';
      case 'gamma': return '#a78bfa';
      default: return '#94a3b8';
    }
  };

  const getDecayModeLabel = (mode: RadiationType): string => {
    switch (mode) {
      case 'stable': return t('stable');
      case 'alpha': return t('decayAlpha');
      case 'beta-': return t('decayBetaMinus');
      case 'beta+': return t('decayBetaPlus');
      case 'gamma': return t('decayGamma');
      case 'neutron': return t('decayNeutron');
      case 'proton': return t('decayProton');
      default: return mode;
    }
  };

  return (
    <div
      className="p-4 rounded-lg border transition-all hover:shadow-lg"
      style={{
        backgroundColor: `${elementColor}10`,
        borderColor: isotope.isStable ? '#4ade80' : '#f87171',
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold" style={{ color: elementColor }}>
            {isotope.symbol}
          </span>
          <Badge
            variant="outline"
            className="text-xs"
            style={{
              borderColor: isotope.isStable ? '#4ade80' : '#f87171',
              color: isotope.isStable ? '#4ade80' : '#f87171',
            }}
          >
            {isotope.isStable ? (
              <><Shield className="w-3 h-3 mr-1" />{t('stable')}</>
            ) : (
              <><Radiation className="w-3 h-3 mr-1" />{t('radioactive')}</>
            )}
          </Badge>
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-[#a8b2d1]">{t('mass')}</span>
          <span className="text-white font-medium">{isotope.massNumber}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#a8b2d1]">{t('halfLife')}</span>
          <span className="text-white font-medium">{displayHalfLife}</span>
        </div>
        {isotope.naturalAbundance !== undefined && (
          <div className="flex justify-between items-center">
            <span className="text-[#a8b2d1]">{t('abundance')}</span>
            <span className="text-white font-medium">{isotope.naturalAbundance.toFixed(2)}%</span>
          </div>
        )}
        <div className="pt-2 border-t border-[#233554]">
          <span className="text-[#a8b2d1] text-xs block mb-2">{t('radiation')}</span>
          <div className="flex flex-wrap gap-1">
            {isotope.decayMode.map((mode, idx) => (
              <Badge
                key={idx}
                variant="secondary"
                className="text-xs"
                style={{
                  backgroundColor: `${getDecayModeColor(mode)}20`,
                  color: getDecayModeColor(mode),
                  borderColor: getDecayModeColor(mode),
                  borderWidth: '1px',
                }}
              >
                {getDecayModeLabel(mode)}
              </Badge>
            ))}
          </div>
        </div>
        {(displayUses) && (
          <div className="pt-2 border-t border-[#233554]">
            <span className="text-[#a8b2d1] text-xs block mb-1">{t('isotopeUses')}</span>
            <p className="text-white text-xs leading-relaxed">{displayUses}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function IonCard({ ion, elementColor, atomicNumber, ionIndex }: { ion: Ion; elementColor: string; atomicNumber: number; ionIndex: number }) {
  const t = useTranslations('Element');
  const locale = useLocale();
  const ionEn = locale === 'en' ? elementIonsEn[atomicNumber]?.[ionIndex] : null;
  const displayName = ionEn?.name ?? ion.name;
  const displayUses = ionEn?.uses ?? ion.uses;
  const isCation = ion.type === 'cation';
  const borderColor = isCation ? '#60a5fa' : '#4ade80';
  const badgeColor = isCation ? '#60a5fa' : '#4ade80';
  const badgeLabel = isCation ? 'Cation ⊕' : 'Anion ⊖';

  const chargeDisplay =
    Math.abs(ion.charge) === 1
      ? isCation ? '⁺' : '⁻'
      : isCation
        ? `${Math.abs(ion.charge)}⁺`
        : `${Math.abs(ion.charge)}⁻`;

  return (
    <div
      className="p-4 rounded-lg border transition-all hover:shadow-lg"
      style={{ backgroundColor: `${elementColor}10`, borderColor }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span
            className="text-3xl font-bold tracking-tight"
            style={{ color: elementColor, textShadow: `0 0 12px ${elementColor}60` }}
          >
            {ion.symbol}
          </span>
        </div>
        <Badge
          variant="outline"
          className="text-xs font-semibold"
          style={{ borderColor: badgeColor, color: badgeColor }}
        >
          {badgeLabel}
        </Badge>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between items-center">
          <span className="text-[#a8b2d1]">{t('ion')}</span>
          <span className="text-white font-medium">{displayName}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-[#a8b2d1]">{t('charge')}</span>
          <span className="font-bold text-base" style={{ color: badgeColor }}>{chargeDisplay}</span>
        </div>
        <div className="pt-2 border-t border-[#233554]">
          <span className="text-[#a8b2d1] text-xs block mb-1">{t('ionUses')}</span>
          <p className="text-white text-xs leading-relaxed">{displayUses}</p>
        </div>
      </div>
    </div>
  );
}

function PropertyRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-[#233554]">
      <span className="text-[#a8b2d1]">{label}:</span>
      <span className="text-white font-medium">{value}</span>
    </div>
  );
}
